import { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import partialRight from 'lodash/partialRight';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import { getRequiresFallback } from '../../DaEditorWrapper/redux/selectors';
import { getIsFocusRequested, getPublishResult, isBusy as isBusySelector, } from '../redux/selectors';
import { CommentEditorSource } from '../redux/types';
import { useEditorDraftUnmountEffects } from './useEditorDraftUnmountEffects';
import { usePublishSuccessCleanup } from './usePublishSuccessCleanup';
import { useEditorDraftState } from './useEditorDraftState';
import { useForceLoadIfNeeded } from './useForceLoadIfNeeded';
/** Provides all the state and effects you'll need for a Comment editor! */
export function useCommentEditor(commentBoxId, commentableItemId, commentableType, _a) {
    var _b = _a === void 0 ? {
        editorSource: CommentEditorSource.Independent,
    } : _a, editorSource = _b.editorSource, relevantComment = _b.relevantComment, autoFocus = _b.autoFocus, initiallyExpanded = _b.initiallyExpanded, onPublishSuccess = _b.onPublishSuccess;
    var isFocusRequested = useSelector(partialRight(getIsFocusRequested, commentBoxId, autoFocus));
    var logBiEvent = useBiLogger();
    var triggerLoad = useContext(LoadableComponentContext).triggerLoad;
    var _c = useState(initiallyExpanded ||
        isFocusRequested ||
        editorSource === CommentEditorSource.Edit), isExpanded = _c[0], setIsExpanded = _c[1];
    // Handle everything for expanding a comment box:
    //  - Logging BI
    //  - Updating the loading context so that our CommentEditor chunk gets loaded
    //  - Updating the local state's isExpanded indicator
    var expandEditor = useCallback(function () {
        logBiEvent({
            evid: BiEventType.CLICK_ON_COMMENTS_TO_START_TYPING,
            itemid: typeof commentableItemId === 'string'
                ? parseInt(commentableItemId)
                : commentableItemId,
            typeid: commentableType,
        });
        triggerLoad();
        setIsExpanded(true);
    }, [logBiEvent, commentableItemId, commentableType, triggerLoad]);
    var collapseEditor = useCallback(function () { return setIsExpanded(false); }, []);
    var editorDraftState = useEditorDraftState(commentBoxId);
    useCommentEditorEffects({
        editorId: commentBoxId,
        isFocusRequested: isFocusRequested,
        expandEditor: expandEditor,
        collapseEditor: collapseEditor,
        onPublishSuccess: onPublishSuccess,
        editorSource: editorSource,
        hasInitialContent: editorSource === CommentEditorSource.Edit || !!editorDraftState,
    });
    // We need to figure out what the content of the comment box should be on mount. There are two options:
    //  1. A draft that the user started, but left the page
    //  2. The content of an existing comment that the user is editing.
    var initialContent = null;
    if (editorDraftState) {
        initialContent = editorDraftState;
    }
    else if (editorSource === CommentEditorSource.Edit && relevantComment) {
        initialContent = relevantComment.textContent;
    }
    var _d = useState(!initialContent), isFallbackEmpty = _d[0], setIsFallbackEmpty = _d[1];
    // Now some values from redux we pass straight through
    var isBusy = useSelector(partialRight(isBusySelector, commentBoxId));
    var shouldUseFallback = useSelector(getRequiresFallback);
    return {
        editorSource: editorSource,
        isBusy: isBusy,
        shouldUseFallback: shouldUseFallback,
        isFallbackEmpty: isFallbackEmpty,
        setIsFallbackEmpty: setIsFallbackEmpty,
        relevantComment: relevantComment,
        isFocusRequested: isFocusRequested,
        isExpanded: isExpanded,
        expandEditor: expandEditor,
        collapseEditor: collapseEditor,
        initialContent: initialContent,
    };
}
/**
 * Handles all the effects that a comment editor needs to handle.
 *
 * Some of what it handles:
 *  - Draft saving/deleting
 *  - Error handling
 *  - Expand on focus
 *  - Collapse on publish
 *  - and more!
 */
function useCommentEditorEffects(_a) {
    var editorId = _a.editorId, isFocusRequested = _a.isFocusRequested, collapseEditor = _a.collapseEditor, expandEditor = _a.expandEditor, onPublishSuccess = _a.onPublishSuccess, editorSource = _a.editorSource, hasInitialContent = _a.hasInitialContent;
    useEditorDraftUnmountEffects(editorId);
    var publishResult = useSelector(partialRight(getPublishResult, editorId));
    // Make sure we trigger onPublishSuccess if publishResult comes back successful
    // TODO: Run this in a saga somewhere instead?
    usePublishSuccessCleanup(editorId, publishResult, onPublishSuccess);
    useEffect(function () {
        if (publishResult) {
            collapseEditor();
        }
    }, [publishResult, collapseEditor]);
    useEffect(function () {
        if (isFocusRequested) {
            expandEditor();
        }
    }, [isFocusRequested, expandEditor]);
    useForceLoadIfNeeded(editorSource, isFocusRequested, hasInitialContent);
}
//# sourceMappingURL=useCommentEditor.js.map